import { ReactElement } from "react";
import { Severity } from "./MessageContainer";
import { ErrorCircle20Filled, Info20Filled, Warning20Filled, CheckmarkCircle20Filled } from "@fluentui/react-icons";

export type Props = {
  title: string;
  content: string;
  className?: string;
  severity: Severity;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;

  primaryAction?: () => void;
  secondaryAction?: () => void;
};

type SeverityStyles = {
  Icon: ReactElement;
  borderColor: string;
  backgroundColor: string;
  foregroundColor: string;
};

const getSeverityStyles = (severity: Severity): SeverityStyles => {
  switch (severity) {
    case Severity.Success: {
      return {
        backgroundColor: "bg-descriptive-green-100",
        foregroundColor: "text-descriptive-green-800",
        borderColor: "border-l-descriptive-green-800",
        Icon: <CheckmarkCircle20Filled className="w-5 h-5" />,
      };
    }
    case Severity.Error: {
      return {
        backgroundColor: "bg-descriptive-red-50",
        foregroundColor: "text-descriptive-red-650",
        borderColor: "border-l-descriptive-red-650",
        Icon: <ErrorCircle20Filled className="w-5 h-5" />,
      };
    }
    default:
    case Severity.Information: {
      return {
        backgroundColor: "bg-primary-cyan-40",
        foregroundColor: "text-primary-blue-660",
        borderColor: "border-l-primary-blue-660",
        Icon: <Info20Filled className="w-5 h-5" />,
      };
    }
    case Severity.Warning: {
      return {
        backgroundColor: "bg-descriptive-yellow-150",
        foregroundColor: "text-descriptive-yellow-850",
        borderColor: "border-l-descriptive-yellow-850",
        Icon: <Warning20Filled className="w-5 h-5" />,
      };
    }
  }
};

export const MessagePresentational = (props: Props) => {
  const { backgroundColor, foregroundColor, borderColor, Icon } = getSeverityStyles(props.severity);

  return (
    <div className={`flex p-2 rounded border-l-4 text-white ${backgroundColor} ${borderColor} ${props.className}`}>
      <div className={`mr-2 self-start ${foregroundColor}`}>{Icon}</div>
      <div className="flex flex-col justify-center">
        <div className={`mt-0.5 font-semibold ${foregroundColor}`}>{props.title}</div>
        <span className="text-black mt-2 whitespace-pre-line">{props.content}</span>
        {(props.primaryActionLabel || props.secondaryActionLabel) && (
          <div className="mt-4">
            {props.primaryActionLabel && (
              <button className="mr-2 text-primary-blue-560 text-sm mr-6 font-semibold" onClick={props.primaryAction}>
                {props.primaryActionLabel}
              </button>
            )}
            {props.secondaryActionLabel && (
              <button className="text-black text-sm mr-6 font-semibold" onClick={props.secondaryAction}>
                {props.secondaryActionLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
